import {
    Box,
    CircularProgress,
    Divider,
    FormControl,
    FormHelperText,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";
import { useTheme } from "@mui/system";
import React, { useState } from "react";
import TreffasButton from "src/components/TreffasButton";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import http from "src/utils/http";

import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller as Fc } from "react-hook-form";
import TreffasStyle from "src/utils/TreffasStyle";

import { useTranslation } from "react-i18next";
import { useSnackbarContext } from "src/context/SnackbarContext";
import { ArrowRight, Mail } from "react-feather";

const MagicLinkLogin = ({ setFormtype }) => {
    const themes = useTheme();
    const navigate = useNavigate();
    const [isProcess, setIsProcess] = useState(false);

    const { t } = useTranslation();
    const { showSnackbar } = useSnackbarContext();
    const [showLink, setShowLink] = useState({
        enable: false,
        email: "",
    });

    const [isVerifyProcess, setIsVerifyProcess] = useState(false);

    const baseURL = window.location.origin;

    const Schema = Yup.object({
        email: Yup.string()
            .email(t("login.form.validation.email_invalid"))
            .required(t("login.form.validation.email_required")),
    }).required();

    const {
        handleSubmit,
        control,
        setError,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(Schema),
        defaultValues: {
            email: "",
        },
    });

    const handleResentVerification = () => {
        let email = showLink.email;
        setIsVerifyProcess(true);
        http
            .post("/api/oauth/registration/sent-verification-email", {
                email: email,
                name: null,
                base_url: baseURL,
            })
            .then(({ data }) => {
                if (data.message === "success") {
                    showSnackbar(
                        t("registration.form.validation.verification-sent"),
                        "success"
                    );
                }
            })
            .catch((err) => {
                console.log(`error:`, err.message);
            })
            .finally(() => setIsVerifyProcess(false));
    };

    const onSendMagicLink = (values) => {
        setIsProcess(true);
        http
            .post("/api/oauth/magic-link/send", {
                email: values.email.trim().toLowerCase(),
                base_url: baseURL,
            })
            .then(({ data }) => {
                if (data.message === "success") {
                    showSnackbar(
                        "Magic link sent successfully! Check your email.",
                        "success"
                    );

                    navigate("/login/magic-link/success?email=" + values.email);
                }

                if (data.message === "email-not-found") {
                    setError("email", {
                        type: "required",
                        message: "Email not found.",
                    });
                }

                if (data.message === "account-unverified") {
                    setShowLink({
                        enable: true,
                        email: values.email.trim().toLowerCase(),
                    });

                    setError("submit", {
                        type: "required",
                        message: t("login.form.validation.account_unverified"),
                    });
                }
            })
            .catch((err) => {
                console.log(err.message);
                setError("submit", {
                    type: "required",
                    message: "Failed to send magic link. Please try again.",
                });
            })
            .finally(() => setIsProcess(false));
    };

    return (
        <div>
            <form onSubmit={handleSubmit(onSendMagicLink)}>
                <Box sx={{ mt: "24px" }}>
                    <FormControl fullWidth variant="standard">
                        <Typography
                            sx={{
                                ...TreffasStyle.formLabel,
                                fontFamily: "var(--font-work-sans)",
                            }}
                        >
                            {t("login.form.email")}
                        </Typography>

                        <Fc
                            id="login_magiclink_email_input"
                            name="email"
                            control={control}
                            rules={{
                                required: true,
                            }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    placeholder={"Enter email address"}
                                    type="email"
                                    sx={TreffasStyle.formInput(themes)}
                                    error={Boolean(errors.email)}
                                    helperText={errors.email?.message}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="end">
                                                <Mail size={16} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </FormControl>


                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 1, gap: 1 }}>
                        {showLink.enable && (
                            <>
                                <Typography
                                    sx={{
                                        fontSize: "12px",
                                        lineHeight: "31px",
                                        textAlign: "center",
                                        color: "var(--color-brand-primary)",
                                        cursor: "pointer",
                                        fontFamily: "var(--font-grenettepro)",
                                    }}
                                    onClick={() => {
                                        if (!isVerifyProcess) {
                                            handleResentVerification();
                                        }
                                    }}
                                >
                                    {isVerifyProcess && (
                                        <CircularProgress size={14} color="inherit" />
                                    )} Resent verification
                                </Typography>

                                <Divider orientation="horizontal" sx={{ borderRight: "1px solid #0000001f", height: "18px" }} />
                            </>
                        )}

                        <Typography
                            sx={{
                                fontSize: "12px",
                                lineHeight: "31px",
                                textAlign: "center",
                                color: "var(--color-brand-primary)",
                                cursor: "pointer",
                                fontFamily: "var(--font-grenettepro)",
                            }}
                            onClick={() => setFormtype("default")}
                        >
                            Form Login
                        </Typography>

                    </Box>




                </Box>

                {errors.submit && (
                    <Box>
                        <FormHelperText
                            error
                            sx={{
                                textAlign: "center",
                            }}
                        >
                            {errors.submit.message}
                        </FormHelperText>
                    </Box>
                )}

                <Box sx={{ mt: "24px" }}>
                    <TreffasButton
                        id="login_magiclink_send_button"
                        variant="primary"
                        size="md"
                        fullWidth
                        type="submit"
                        loading={isProcess}
                        endIcon={<ArrowRight size={16} />}
                    >
                        Send Magic Link
                    </TreffasButton>
                </Box>

                <Box sx={{ mt: "24px", pb: "24px" }}>
                    <Typography
                        sx={{
                            fontWeight: 400,
                            fontSize: "14px",
                            lineHeight: "24px",
                            textAlign: "center",
                            color: "#525F7F",
                        }}
                    >
                        {t("login.form.signup_text")}
                        <Typography
                            variant="subtitle"
                            color="primary"
                            sx={{
                                cursor: "pointer",
                                marginLeft: 0.5,
                                marginRight: 0.5,
                                color: "var(--color-brand-primary)",
                                fontFamily: "var(--font-grenettepro)",
                            }}
                            onClick={() => navigate("/register")}
                        >
                            {t("login.form.signup_btn")}
                        </Typography>
                        {t("login.form.signup_text_cont")}
                    </Typography>
                </Box>
            </form>
        </div>
    );
};

export default MagicLinkLogin;
